/* eslint-disable security/detect-object-injection */
import { BareFetcher } from 'swr'
import { isJokerPageResolution } from '@/core/utils/joker/pageResolution'
import { RawPageTemplate } from '../types'

type TemplateCache = {
  [key: string]: { data: RawPageTemplate; expires: number }
}
type CacheStatus = 'HIT' | 'MISS' | 'STALE' | 'EXPIRED'

export interface TemplateResultData {
  name?: string
  locales?: unknown[]
  serverProps?: Record<string, unknown>
  header?: Record<string, unknown>
  blocks?: unknown[]
  featureFlags?: Record<string, unknown>
  meta?: Record<string, unknown>
  locale?: string
}

export interface TemplateRequestLog {
  id: string
  templateName: string
  params: Record<string, unknown>
  cacheKey: string
  cacheStatus: CacheStatus
  startTime: number
  endTime: number
  duration: number
  timestamp: Date
  success: boolean
  error?: string
  data?: TemplateResultData
}

const MAX_LOGS = 50

export class TemplateService {
  private fetcher: BareFetcher<RawPageTemplate>
  static cache: TemplateCache = {}
  static requestLogs: TemplateRequestLog[] = []
  static loggingEnabled = false
  private readonly TTL = 10 * 60 * 1000

  constructor(fetcher: BareFetcher<RawPageTemplate>) {
    if (fetcher) {
      this.fetcher = fetcher
    } else {
      throw new Error('Invalid fetcher')
    }
  }

  private static generateId(): string {
    return `${Date.now()}-${crypto.randomUUID()}`
  }

  private static addLog(log: TemplateRequestLog): void {
    TemplateService.requestLogs.unshift(log)
    if (TemplateService.requestLogs.length > MAX_LOGS) {
      TemplateService.requestLogs = TemplateService.requestLogs.slice(0, MAX_LOGS)
    }
  }

  private getCacheKey(
    templateType: 'pages' | 'blocks',
    templateName: string,
    templateParams: Record<string, unknown>
  ): string {
    const query = new URLSearchParams(
      Object.entries(templateParams).map(([key, value]) => [key, value.toString()])
    )
    return `api/${templateType}/${templateName}?${query}`
  }

  private determineCacheStatus(key: string, useCache: boolean): CacheStatus {
    if (!useCache) return 'MISS'

    const cached = TemplateService.cache[key]

    if (!cached) return 'MISS'

    if (cached.expires > Date.now()) return 'HIT'
    if (cached.data) return 'STALE'

    return 'EXPIRED'
  }

  async getTemplate(
    templateName: string,
    templateParams: Record<string, unknown>,
    useCache = true,
    devToolsEnabled = false
  ): Promise<RawPageTemplate> {
    const startTime =
      typeof performance !== 'undefined' ? performance.now() : Date.now()
    const timestamp = new Date()
    const key = this.getCacheKey('pages', templateName, templateParams)
    const cacheStatus = this.determineCacheStatus(key, useCache)

    let success = true
    let errorMsg: string | undefined
    let result: RawPageTemplate = null

    try {
      if (useCache) {
        const cached = TemplateService.cache[key]

        if (cached) {
          if (cached.expires > Date.now()) {
            result = cached.data
          } else {
            this.refreshTemplate(key)
            result = cached.data
          }
        } else {
          result = await this.refreshTemplate(key)
        }
      } else {
        result = await this.fetchTemplate(key)
      }
    } catch (error) {
      success = false
      errorMsg = error instanceof Error ? error.message : 'Unknown error'
      throw error
    } finally {
      if (devToolsEnabled) {
        const endTime =
          typeof performance !== 'undefined' ? performance.now() : Date.now()

        const templateData: TemplateResultData | undefined = result
          ? {
              name: result.name,
              locales: (result as any).locales,
              serverProps: (result as any).serverProps,
              header: (result as any).header,
              blocks: (result as any).blocks,
              featureFlags: (result as any).featureFlags,
              meta: (result as any).meta,
              locale: (result as any).locale,
            }
          : undefined

        const log: TemplateRequestLog = {
          id: TemplateService.generateId(),
          templateName: result?.name || templateName,
          params: templateParams,
          cacheKey: key,
          cacheStatus,
          startTime,
          endTime,
          duration: Math.round((endTime - startTime) * 100) / 100,
          timestamp,
          success,
          error: errorMsg,
          data: templateData,
        }

        TemplateService.addLog(log)
      }
    }

    return result
  }

  private async fetchTemplate(key: string): Promise<RawPageTemplate> {
    try {
      return await this.fetcher(key)
    } catch (error) {
      if (error.response && error.response.status === 401) {
        throw new Error('Unauthorized')
      }
      console.error('Failed to fetch template:', error.message)
      return null
    }
  }

  private async refreshTemplate(key: string): Promise<RawPageTemplate> {
    const data = await this.fetchTemplate(key)
    if (data && !isJokerPageResolution(data)) {
      TemplateService.cache[key] = {
        data,
        expires: Date.now() + this.TTL,
      }
    }
    return data
  }

  static clearLogs(): void {
    TemplateService.requestLogs = []
  }

  static getLogs(): TemplateRequestLog[] {
    return [...TemplateService.requestLogs]
  }
}
